import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { ISdFormComponentItem } from '../form-component-interface.model';
import { SdFormCheckboxGroupComponent } from '../form-checkbox-group/form-checkbox-group.component';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { SdFormAutocompleteComponent } from '../form-autocomplete/form-autocomplete.component';
import { SdFormLibelleComponent } from '../form-libelle/form-libelle.component';
import { PositionEnum } from '../../../_enums/position.enum';
import { ThemesEnum } from '../../../_enums/themes.enum';
import { SdThemeComponent } from '../../abstract/theme/theme.component';
import { CoreModalService } from '../../modals/core-modal/core-modal.service';
import { SdTagComponent } from '../../tags/tag/tag.component';
import { ErrorTranslateService } from '../error-translate-service';
import { BaseFormErrorTranslateService } from '../form-common-error/base-form-error-translate.service';
import { DropdownType } from '../../dropdowns/dropdown/dropdown.enum';
import { SdDropdownComponent } from '../../dropdowns/dropdown/dropdown.component';
import { SdListComponent } from '../../lists/list/list.component';

/**
 * Component of select.
 * The component is used in the following way:
 * <sd-form select
 *  [nameField]="'country_1'"
 *  [labelTitle]="'Liste de pays'"
 *  [items]="countryList"
 *  [required]="true"
 *  formControlName="select"
 * </sd-form>
 */
@Component({
  selector: 'sd-form[select-multiple]',
  templateUrl: './form-select-multiple.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdFormLibelleComponent,
    SdDropdownComponent,
    SdFormAutocompleteComponent,
    SdFormCheckboxGroupComponent,
    NgTemplateOutlet,
    SdListComponent,
    SdTagComponent
  ],
  providers: [
    { provide: BaseFormErrorTranslateService, useClass: ErrorTranslateService },
    { provide: CoreModalService }
  ]
})
export class SdFormSelectMultipleComponent extends SdThemeComponent implements OnInit {
  /**
   * Label of the form element
   */
  @Input()
  labelTitle: string;

  /**
   * Allows to hide the label
   */
  @Input()
  labelHidden = false;

  /**
   * ID of the form element (html attribute)
   */
  @Input()
  idField = '';

  /**
   * Name of the form element (html attribute)
   */
  @Input()
  nameField: string;

  /**
   * Key representing the unique identifier of the object
   */
  @Input()
  valueKey: string;

  /**
   * Key of the objects to be used as display for each "option
   * Can be a getter on the object that returns a formatted label
   */
  @Input()
  libelleKey: string;

  @Input()
  isSmall = false;

  @Input()
  required = false;

  @Input()
  placeholder: string | boolean;

  @Input()
  isOpened = false;

  @Input()
  displaySelection = false;

  @Input()
  displayWidth: number = null;

  @Output()
  isOpenedChange = new EventEmitter<boolean>();

  @Output()
  formChange = new EventEmitter<ISdFormComponentItem>();

  @ViewChild('checkbox')
  checkbox: SdFormCheckboxGroupComponent;

  @ViewChild('dropdown')
  dropdown: SdDropdownComponent;

  /**
   * Allows to enter the label in the keyboard focus (tab)
   */
  @Input()
  labelItemFocusable = false;

  /**
   * List of possible options
   */
  @Input()
  items: ISdFormComponentItem[];

  @Input()
  hasAutocomplete = false;

  @Input()
  groupLibelleTemplate: TemplateRef<any>;

  @Input() class = '';
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.labelTitle && !this.labelHidden ? 'has-libelle' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  currentValue: ISdFormComponentItem[];
  buttonSize: number;
  positionEnum = PositionEnum;
  themesEnum = ThemesEnum;
  dropdownType = DropdownType.select;
  selectedItems: ISdFormComponentItem[];

  constructor(private coreModalService: CoreModalService) {
    super();
  }

  ngOnInit(): void {
    this.items?.map((el) => (el.nameField = this.nameField));
    this.selectedItems = this.items?.filter((el) => el.checked === true);

    if (isPlatformBrowser(this.platformId) && this.displayWidth) {
      this.elRef.nativeElement.style.setProperty(
        'width',
        this.displayWidth / 10 + 'rem',
      );
    }
  }

  onChange(event: ISdFormComponentItem): void {
    this.formChange.emit(event);

    if (!this.hasAutocomplete) {
      this.close();
    }

    this.selectedItems = this.items?.filter((el) => el.checked === true);
  }

  close(): void {
    this.coreModalService.closeAllModal();
  }

  deleteSelection(item: ISdFormComponentItem): void {
    item.checked = false;
    this.onChange(item);
  }

  /**
   * Allow to update dropdown modal position
   */
  updatePosition(): void {
    this.dropdown.updatePosition();
  }
}
